import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api, API } from "@/lib/api";
import { MapPinned, HandHeart, Coffee, Quote } from "lucide-react";
import dayjs from "dayjs";

// Phase 17-C item 9: public SEO page for an admin-managed area.
// Live counts only (aggregates — never fabricated activity), latest real
// stories from public-profile neighbours in the area, and ONE CTA.
export default function VolunteeringIn() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArea(null); setNotFound(false);
    api.publicArea(slug).then(setArea).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="area-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl text-forest">We're not in that area yet</h1>
        <p className="text-neutral-600 mt-3">We're growing street by street — check back soon.</p>
        <Link to="/collectives" data-testid="area-not-found-back" className="inline-block mt-6 text-forest font-medium underline">Browse collectives</Link>
      </div>
    );
  }
  if (!area) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const stats = [
    { icon: HandHeart, label: "Open roles", value: area.open_roles, testId: "area-stat-roles" },
    { icon: MapPinned, label: "Collectives", value: area.collectives, testId: "area-stat-collectives" },
    { icon: Coffee, label: "Upcoming gatherings", value: area.upcoming_gatherings, testId: "area-stat-gatherings" },
  ];

  return (
    <div className="bg-cream min-h-screen" data-testid="area-page">
      <Seo
        title={`Volunteering in ${area.name}`}
        description={`Live volunteering opportunities, neighbourhood collectives and neighbour stories in ${area.name}. Every hour counts, and gets counted.`}
        image={`${API}/og/place/${area.slug}`}
      />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Volunteering in</div>
        <h1 className="font-display text-4xl sm:text-5xl text-forest mt-2 tracking-tight">{area.name}</h1>
        <p className="text-neutral-600 mt-4 max-w-2xl text-lg">
          Neighbours in {area.name} are giving time, asking for a hand and organising their streets on CivicHearth.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={s.testId} className="bg-white border border-neutral-200 rounded-2xl p-6">
              <s.icon className="w-5 h-5 text-terracotta" />
              <div className="font-display text-4xl text-forest mt-3">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-forest/60 font-bold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {area.stories?.length > 0 && (
          <div className="mt-12" data-testid="area-stories">
            <h2 className="text-base md:text-lg font-semibold text-forest">Latest from neighbours</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {area.stories.map((s) => (
                <div key={s.id} className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <Quote className="w-4 h-4 text-terracotta" />
                  <p className="text-sm text-neutral-700 mt-2 line-clamp-4">{s.text}</p>
                  <div className="text-xs text-neutral-500 mt-3">
                    — {s.user_name}{s.created_at ? ` · ${dayjs(s.created_at).format("MMM YYYY")}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ONE CTA (item 9 spec) */}
        <div className="mt-12 bg-forest rounded-2xl p-8 sm:p-10 text-white flex items-center justify-between flex-wrap gap-6">
          <div>
            <div className="font-display text-2xl">Ready to give an hour in {area.name}?</div>
            <p className="text-white/70 text-sm mt-1.5">Verified causes and neighbours who need a hand — right where you live.</p>
          </div>
          <Link to={`/opportunities?q=${encodeURIComponent(area.name)}`}>
            <Button data-testid="area-cta" className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">
              Find opportunities
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
